import { useEffect, useRef, useState, useCallback } from 'react';
import { Mic, MicOff, Video as VideoOn, VideoOff, PhoneOff, User, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getToken } from '@/lib/tokenStore';
import { playVoiceOver, useVoiceOverPlaying } from '@/lib/voiceOver';
import { diagnoseMicSupport, queryMicPermission, getUnblockInstructions, isMobile } from '@/lib/browserDetect';
import { startMediaSessionKeepalive, stopMediaSessionKeepalive, armMediaSessionOnGesture } from '@/lib/mediaSessionKeepalive';

/**
 * VideoRoom - fully self-hosted mesh WebRTC.
 * ICE servers fetched from backend (which proxies Metered.ca dynamic credentials).
 * Backend signaling: WS at /api/ws/rtc/{groupId}?token={jwt}
 * Best for 2-6 participants (mesh scales O(n^2)).
 */
// Danish voice-over MP3s (played on join / leave for every group)
const VOICE_WELCOME_URL = 'https://customer-assets-lqy194kg.emergentagent.net/job_unified-video-chat/artifacts/xcv9jt2v_davinci_velkommen_til_samtalegruppen___jeg__nsker_dig_god_.mp3';
const VOICE_FAREWELL_URL = 'https://customer-assets-lqy194kg.emergentagent.net/job_unified-video-chat/artifacts/f9t6ob0h_davinci_tak_for_at_du_deltog_i_samtalen_i_gruppen___du_er_.mp3';

// ─────────────────────────────────────────────────────────────────────────────
// WebRTC debug logging
// Turn on by appending ?rtcdebug=1 to the URL (persists via localStorage), or
// call `window.__setRtcDebug(true)` in the console. Off by default in prod so
// end-user consoles stay clean.
// Log tag: [rtc] — filter your DevTools console by "[rtc]" to see every step.
// ─────────────────────────────────────────────────────────────────────────────
const RTC_DEBUG_KEY = 'sam247_rtc_debug';
const isRtcDebug = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (window.location.search.includes('rtcdebug=1')) {
      localStorage.setItem(RTC_DEBUG_KEY, '1');
      return true;
    }
    if (window.location.search.includes('rtcdebug=0')) {
      localStorage.removeItem(RTC_DEBUG_KEY);
      return false;
    }
    return localStorage.getItem(RTC_DEBUG_KEY) === '1';
  } catch { return false; }
};
if (typeof window !== 'undefined') {
  window.__setRtcDebug = (on) => {
    try {
      if (on) localStorage.setItem(RTC_DEBUG_KEY, '1');
      else localStorage.removeItem(RTC_DEBUG_KEY);
      // eslint-disable-next-line no-console
      console.info(`[rtc] debug logging ${on ? 'ON' : 'OFF'} — reload the page`);
    } catch { /* noop */ }
  };
}
const rtcLog = (...args) => {
  if (!isRtcDebug()) return;
  // eslint-disable-next-line no-console
  console.log('[rtc]', ...args);
};
const rtcWarn = (...args) => {
  if (!isRtcDebug()) return;
  // eslint-disable-next-line no-console
  console.warn('[rtc]', ...args);
};
// Always-on logging: prints regardless of ?rtcdebug flag. Used for TURN/ICE
// diagnostics we always want visible so end users can send us the log when
// something breaks. Keep this SET SHORT — everything else should stay behind
// isRtcDebug() to keep the console clean.
const rtcAlways = (...args) => {
  // eslint-disable-next-line no-console
  console.log('%c[rtc]', 'color:#c0f000;font-weight:bold', ...args);
};
const rtcAlwaysWarn = (...args) => {
  // eslint-disable-next-line no-console
  console.warn('%c[rtc]', 'color:#ff5c5c;font-weight:bold', ...args);
};
// Pretty-print an ICE candidate string into { type, protocol, address, port }
const parseCandidate = (candidate) => {
  if (!candidate || !candidate.candidate) return null;
  // Prefer the parsed props exposed by the browser when available.
  return {
    type: candidate.type || 'unknown',       // host | srflx | prflx | relay
    protocol: candidate.protocol || 'unknown',
    address: candidate.address || candidate.ip || '?',
    port: candidate.port || '?',
    foundation: candidate.foundation || '?',
    priority: candidate.priority,
    raw: candidate.candidate,
  };
};

export default function VideoRoom({ groupId, groupName, displayName, myNickname, withVideo = true, guestId = null, forceMuted = false, skipFarewell = false, showConnectedCount = false, hideParticipantInfo = false, showConnectionStatus = false, onLeave }) {
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const peersRef = useRef({}); // peerId -> { pc, name, videoEl }
  const myPeerIdRef = useRef(null);

  const [remotePeers, setRemotePeers] = useState({}); // peerId -> { stream, name }
  // Live set of peer IDs from WS signaling. This is separate from `remotePeers`
  // (which only fires on ontrack): silent peers (mic off) show here too so the
  // counter reflects actual participants.
  const [connectedPeers, setConnectedPeers] = useState({}); // peerId -> name
  // Mic starts OFF: on mobile especially, we don't want to prompt for permission
  // at mount. Permission is requested on first click of the mic button.
  const [micOn, setMicOn] = useState(false);
  const [camOn, setCamOn] = useState(withVideo);
  const [status, setStatus] = useState('starting'); // starting | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  // Non-recoverable browser/environment issue (in-app WebView, HTTP, missing
  // mediaDevices API). Shown as a persistent overlay because a toast is easy
  // to miss on mobile and the user needs specific instructions.
  const [micBlockNotice, setMicBlockNotice] = useState(null); // { title, message, appName? }
  // Live per-peer WebRTC connection state. Drives the on-screen status pill
  // when `showConnectionStatus=true`. Keys are peer IDs, values are
  // RTCPeerConnection.connectionState ('new' | 'connecting' | 'connected'
  // | 'disconnected' | 'failed' | 'closed').
  const [peerConnStates, setPeerConnStates] = useState({});
  // Whether the ICE-servers config fetched from backend actually contains TURN.
  // Set after /api/rtc/ice-servers loads. If false and cross-network peers
  // fail, we surface the reason in the status pill instead of the generic
  // "Forbindelse mislykkedes" message.
  const [turnAvailable, setTurnAvailable] = useState(true);
  const iceServersRef = useRef([]); // populated from backend before any RTCPeerConnection is created
  // Per-peer queue of ICE candidates that arrived BEFORE we could call
  // setRemoteDescription. This is the classic mesh-signaling race:
  // ICE candidates and the SDP offer travel as separate WS messages, and
  // the browser handles them concurrently, so a candidate can beat the
  // offer to the handler. We buffer them here and drain after the remote
  // description is set. Keyed by peerId.
  const pendingCandidatesRef = useRef({}); // { peerId: [candidate, ...] }
  // Tracks whether we have applied the initial remote description for a peer,
  // which is what the ICE-candidate queue is waiting on.
  const remoteDescSetRef = useRef({}); // { peerId: bool }
  // "Perfect negotiation" polite/impolite role, decided by peer_id comparison
  // so both sides agree deterministically who yields on glare.
  const isPoliteFor = useCallback((peerId) => {
    const me = myPeerIdRef.current;
    // Higher peer_id is "polite" (rolls back on collision), lower is "impolite"
    return !!me && me > peerId;
  }, []);
  // Track our own createOffer state to detect collisions
  const makingOfferRef = useRef({}); // { peerId: bool }

  const updatePeerConnState = useCallback((peerId, state) => {
    setPeerConnStates((prev) => {
      if (prev[peerId] === state) return prev;
      return { ...prev, [peerId]: state };
    });
  }, []);

  const setRemoteStream = (peerId, stream, name) => {
    setRemotePeers((prev) => ({ ...prev, [peerId]: { stream, name } }));
  };

  const removePeer = useCallback((peerId) => {
    // Cancel any pending ICE-restart timer for this peer
    const t = iceRestartTimersRef.current[peerId];
    if (t) { clearTimeout(t); delete iceRestartTimersRef.current[peerId]; }
    delete iceRestartAttemptsRef.current[peerId];
    // Drop any queued ICE candidates that never got a remote description
    delete pendingCandidatesRef.current[peerId];
    // Clear manual-retry banner entry — peer left the room
    setPeersNeedingManualRetry((prev) => {
      if (!(peerId in prev)) return prev;
      const c = { ...prev };
      delete c[peerId];
      return c;
    });
    const p = peersRef.current[peerId];
    if (p) {
      try { p.pc?.close(); } catch (e) { console.debug('pc.close during removePeer:', e); }
      delete peersRef.current[peerId];
    }
    setRemotePeers((prev) => {
      const c = { ...prev };
      delete c[peerId];
      return c;
    });
    setConnectedPeers((prev) => {
      const c = { ...prev };
      delete c[peerId];
      return c;
    });
    setPeerConnStates((prev) => {
      const c = { ...prev };
      delete c[peerId];
      return c;
    });
  }, []);

  // ── ICE restart machinery ──────────────────────────────────────────────
  // When a peer's connection enters 'failed' (immediate) or 'disconnected'
  // (after a 4s grace period), we call pc.restartIce(). That triggers
  // onnegotiationneeded, which crafts a NEW offer with fresh ufrag/pwd — the
  // remote peer receives it via the normal signaling path and responds with
  // an answer. Only one side of the pair should initiate to avoid glare;
  // we use lexicographic order (myPeerId < remotePeerId → we initiate).
  const iceRestartTimersRef = useRef({}); // { peerId: timeoutId }
  const iceRestartAttemptsRef = useRef({}); // { peerId: count } — auto-retry cap is 1
  // Peers whose auto-retry has been exhausted and now need MANUAL retry via
  // the "Forbindelsen mislykkedes — tryk her for at prøve igen" button.
  const [peersNeedingManualRetry, setPeersNeedingManualRetry] = useState({}); // { peerId: name }

  // NOTE: v1.0.24's AudioContext-based mic keepalive was REMOVED in v1.0.26.
  // The silent AudioContext graph (gain=0 → destination) did not qualify as
  // "active audio" for iOS backgrounding and worse, its MediaStreamAudioSourceNode
  // ended up competing with WebRTC for the mic when iOS auto-suspended the
  // context on screen lock — regressing the working screensaver behaviour
  // from v1.0.22. Background mic persistence is handled by the audible-WAV
  // MediaSession trick in lib/mediaSessionKeepalive.js (v1.0.20), which is
  // the same technique Zoom/Meet/Discord use.

  const cancelIceRestart = useCallback((peerId) => {
    const t = iceRestartTimersRef.current[peerId];
    if (t) {
      clearTimeout(t);
      delete iceRestartTimersRef.current[peerId];
    }
  }, []);

  const scheduleIceRestart = useCallback((peerId, immediate) => {
    // Bail if we're not the "polite" initiator for this pair — the other side
    // will restart. Guarantees exactly one restart per pair, no glare.
    const me = myPeerIdRef.current;
    if (!me || !(me < peerId)) {
      rtcLog(`peer[${peerId}] not initiator (me=${me}); waiting for other side to restart`);
      return;
    }
    cancelIceRestart(peerId);
    const delay = immediate ? 0 : 4000;
    iceRestartTimersRef.current[peerId] = setTimeout(async () => {
      const entry = peersRef.current[peerId];
      if (!entry || !entry.pc) return;
      const pc = entry.pc;
      const state = pc.iceConnectionState;
      if (state === 'connected' || state === 'completed' || state === 'closed') {
        rtcLog(`peer[${peerId}] recovered before restart (state=${state}) — aborting`);
        return;
      }
      const attempt = (iceRestartAttemptsRef.current[peerId] || 0) + 1;
      iceRestartAttemptsRef.current[peerId] = attempt;
      // Auto-retry ONCE. If that fails too, surface a manual-retry UI so the
      // user can decide whether to try again (fresh PC teardown+recreate) or
      // just leave the group. Automatic infinite retries hide the real problem.
      if (attempt > 1) {
        rtcWarn(`peer[${peerId}] auto-retry exhausted (attempt #${attempt}, state=${state}) — asking user to retry manually`);
        setPeersNeedingManualRetry((prev) => ({ ...prev, [peerId]: entry.name || 'Deltager' }));
        return;
      }
      rtcLog(`peer[${peerId}] restartIce() auto-retry attempt #${attempt} (was ${state})`);
      try {
        if (typeof pc.restartIce === 'function') {
          pc.restartIce();
          // restartIce fires onnegotiationneeded; if the pc is already
          // negotiating, we manually kick off a fresh offer with iceRestart:true
          if (pc.signalingState === 'stable') {
            const offer = await pc.createOffer({ iceRestart: true });
            await pc.setLocalDescription(offer);
            wsRef.current?.send(JSON.stringify({
              type: 'signal', to: peerId, data: { sdp: pc.localDescription },
            }));
            rtcLog(`peer[${peerId}] → sent ICE-restart OFFER`);
          }
        } else {
          // Very old browser fallback
          const offer = await pc.createOffer({ iceRestart: true });
          await pc.setLocalDescription(offer);
          wsRef.current?.send(JSON.stringify({
            type: 'signal', to: peerId, data: { sdp: pc.localDescription },
          }));
        }
      } catch (e) {
        rtcWarn(`peer[${peerId}] ICE restart failed:`, e);
      }
    }, delay);
  }, [cancelIceRestart]);

  const createPeerConnection = useCallback((peerId, name) => {
    // Always-on: dump the exact iceServers array being passed into
    // RTCPeerConnection so we can PROVE TURN is included per peer, not just
    // announced at fetch time.
    rtcAlways(`peer[${peerId}] createPeerConnection with iceServers:`, iceServersRef.current);
    const pc = new RTCPeerConnection({
      iceServers: iceServersRef.current,
      iceCandidatePoolSize: 10,
    });
    // Per-peer counter of local ICE candidate types — used to detect
    // "no TURN candidate was generated" after gathering completes.
    const iceStats = { host: 0, srflx: 0, prflx: 0, relay: 0, other: 0 };

    // Add all local tracks (if the user has already granted mic before this peer joined)
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => {
        try {
          pc.addTrack(t, localStreamRef.current);
          rtcLog(`peer[${peerId}] addTrack (initial): kind=${t.kind}, id=${t.id}, enabled=${t.enabled}`);
        } catch (e) { console.debug('addTrack failed:', e); }
      });
    } else {
      rtcLog(`peer[${peerId}] no local stream yet — connection starts silent (recvonly)`);
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const parsed = parseCandidate(e.candidate);
        // Update per-peer counters and always-log each candidate with its type
        const t = parsed?.type || 'other';
        iceStats[t] = (iceStats[t] || 0) + 1;
        rtcAlways(`peer[${peerId}] LOCAL ICE candidate #${iceStats.host + iceStats.srflx + iceStats.prflx + iceStats.relay + iceStats.other}: type=${t}, protocol=${parsed?.protocol}, address=${parsed?.address}:${parsed?.port}`);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'signal',
            to: peerId,
            data: { candidate: e.candidate },
          }));
        } else {
          rtcAlwaysWarn(`peer[${peerId}] CANNOT SEND ICE candidate — WebSocket not open (readyState=${wsRef.current?.readyState})`);
        }
      } else {
        // Null candidate = end-of-gathering. Print the type breakdown.
        rtcAlways(`peer[${peerId}] LOCAL ICE gathering COMPLETE — types:`, iceStats);
        if (iceStats.relay === 0) {
          rtcAlwaysWarn(`peer[${peerId}] ⚠️ NO TURN (relay) candidates generated. Cross-network peers WILL fail. Either TURN creds are wrong, blocked by firewall, or Metered.ca is unreachable from this network.`);
        }
      }
    };

    pc.onicecandidateerror = (e) => {
      // Always-on so we can see failing TURN URLs immediately.
      rtcAlwaysWarn(`peer[${peerId}] ICE candidate error: url=${e.url}, code=${e.errorCode}, text="${e.errorText}", hostCandidate=${e.hostCandidate}`);
    };

    pc.onicegatheringstatechange = () => {
      rtcLog(`peer[${peerId}] iceGatheringState → ${pc.iceGatheringState}`);
    };

    pc.oniceconnectionstatechange = () => {
      rtcLog(`peer[${peerId}] iceConnectionState → ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'failed') {
        rtcWarn(`peer[${peerId}] ICE FAILED — attempting ICE restart`);
        scheduleIceRestart(peerId, /*immediate*/ true);
      } else if (pc.iceConnectionState === 'disconnected') {
        // Disconnected can be transient (WiFi hiccup, ~5–10s). Only restart
        // if it hasn't recovered after a short grace period.
        rtcLog(`peer[${peerId}] disconnected — will restart ICE in 4s if not recovered`);
        scheduleIceRestart(peerId, /*immediate*/ false);
      } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        // Cancel any pending restart — recovered on its own. Reset the
        // attempt counter so future disconnects get a fresh set of retries.
        cancelIceRestart(peerId);
        iceRestartAttemptsRef.current[peerId] = 0;
        // Clear manual-retry banner if we recovered
        setPeersNeedingManualRetry((prev) => {
          if (!(peerId in prev)) return prev;
          const c = { ...prev };
          delete c[peerId];
          return c;
        });
      }
    };

    pc.onsignalingstatechange = () => {
      rtcLog(`peer[${peerId}] signalingState → ${pc.signalingState}`);
    };

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      const kinds = e.streams?.[0]?.getTracks().map(t => `${t.kind}(${t.id})`).join(',') || 'no-tracks';
      rtcLog(`peer[${peerId}] ontrack — kind=${e.track.kind}, streamId=${stream?.id}, tracks=[${kinds}]`);
      if (stream) setRemoteStream(peerId, stream, name);
    };

    pc.onconnectionstatechange = () => {
      rtcLog(`peer[${peerId}] connectionState → ${pc.connectionState}`);
      updatePeerConnState(peerId, pc.connectionState);
      if (pc.connectionState === 'connected') {
        // Dump the selected candidate pair so we know if we're on host/srflx/relay
        pc.getStats().then((stats) => {
          stats.forEach((r) => {
            if (r.type === 'candidate-pair' && r.state === 'succeeded' && r.nominated) {
              const local = stats.get(r.localCandidateId);
              const remote = stats.get(r.remoteCandidateId);
              rtcLog(`peer[${peerId}] SELECTED CANDIDATE PAIR:`, {
                localType: local?.candidateType,
                localProtocol: local?.protocol,
                localAddress: local?.address || local?.ip,
                remoteType: remote?.candidateType,
                remoteProtocol: remote?.protocol,
                remoteAddress: remote?.address || remote?.ip,
                bytesSent: r.bytesSent,
                bytesReceived: r.bytesReceived,
              });
            }
          });
        }).catch(() => { /* noop */ });
      }
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
        // let peer-left handle cleanup; don't remove aggressively
      }
    };

    // Renegotiate whenever a track is added post-connection (e.g. user turns on mic
    // after joining silently). Guard against glare during initial handshake.
    pc.onnegotiationneeded = async () => {
      try {
        rtcLog(`peer[${peerId}] onnegotiationneeded (signalingState=${pc.signalingState})`);
        if (pc.signalingState !== 'stable') return;
        makingOfferRef.current[peerId] = true;
        const offer = await pc.createOffer();
        if (pc.signalingState !== 'stable') return;
        await pc.setLocalDescription(offer);
        rtcLog(`peer[${peerId}] → sending renegotiation OFFER (type=${pc.localDescription.type})`);
        wsRef.current?.send(JSON.stringify({
          type: 'signal',
          to: peerId,
          data: { sdp: pc.localDescription },
        }));
      } catch (e) {
        rtcWarn(`peer[${peerId}] renegotiation failed:`, e);
      } finally {
        makingOfferRef.current[peerId] = false;
      }
    };

    peersRef.current[peerId] = { pc, name };
    updatePeerConnState(peerId, pc.connectionState || 'new');
    return pc;
  }, [updatePeerConnState, cancelIceRestart, scheduleIceRestart]);

  const initiateOfferTo = useCallback(async (peerId, name) => {
    rtcLog(`peer[${peerId}] initiateOfferTo — name="${name}"`);
    const pc = createPeerConnection(peerId, name);
    try {
      makingOfferRef.current[peerId] = true;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      rtcLog(`peer[${peerId}] → sending initial OFFER (type=${pc.localDescription.type}, sdp length=${pc.localDescription.sdp?.length})`);
      wsRef.current?.send(JSON.stringify({
        type: 'signal',
        to: peerId,
        data: { sdp: pc.localDescription },
      }));
    } catch (e) {
      rtcWarn(`peer[${peerId}] createOffer failed:`, e);
      console.error('createOffer failed', e);
    } finally {
      makingOfferRef.current[peerId] = false;
    }
  }, [createPeerConnection]);

  // Manual retry — user pressed "Prøv igen" on the failure banner.
  // Fully teardown the failed peer connection(s), reset all restart bookkeeping,
  // then start a brand-new offer/answer round. This is more aggressive than
  // pc.restartIce() (which only refreshes ufrag/pwd on the existing PC): we
  // build a fresh RTCPeerConnection with fresh ICE gathering, fresh DTLS keys,
  // and fresh SDP. Used when auto-retry couldn't recover the pair.
  const manualRetryConnection = useCallback(() => {
    const failing = { ...peersNeedingManualRetry };
    if (Object.keys(failing).length === 0) return;
    rtcAlways(`Manual retry initiated for ${Object.keys(failing).length} peer(s):`, Object.keys(failing));
    for (const [peerId, name] of Object.entries(failing)) {
      // 1. Tear down old PC and all bookkeeping
      const t = iceRestartTimersRef.current[peerId];
      if (t) { clearTimeout(t); delete iceRestartTimersRef.current[peerId]; }
      delete iceRestartAttemptsRef.current[peerId];
      delete pendingCandidatesRef.current[peerId];
      delete remoteDescSetRef.current[peerId];
      delete makingOfferRef.current[peerId];
      const p = peersRef.current[peerId];
      if (p?.pc) {
        try { p.pc.close(); } catch { /* noop */ }
      }
      delete peersRef.current[peerId];
      setRemotePeers((prev) => {
        const c = { ...prev };
        delete c[peerId];
        return c;
      });
      setPeerConnStates((prev) => {
        const c = { ...prev };
        delete c[peerId];
        return c;
      });
      // 2. Fresh offer — respect the same "polite/impolite" rule so only one side
      // initiates. If we're impolite (we < peerId), we send. Else we wait for
      // the peer to send us a fresh offer (which they will, because their
      // connection state is also failed and their auto-retry will kick in).
      const me = myPeerIdRef.current;
      if (me && me < peerId) {
        initiateOfferTo(peerId, name);
      } else {
        // Reactive side — just recreate the PC ready to receive an offer.
        createPeerConnection(peerId, name);
      }
    }
    setPeersNeedingManualRetry({});
  }, [peersNeedingManualRetry, initiateOfferTo, createPeerConnection]);

  const handleSignal = useCallback(async (fromPeer, data, fromName) => {
    let pc = peersRef.current[fromPeer]?.pc;
    if (!pc) {
      rtcLog(`peer[${fromPeer}] no existing pc — creating one to handle incoming signal`);
      pc = createPeerConnection(fromPeer, fromName || 'Deltager');
    }

    if (data.sdp) {
      const desc = new RTCSessionDescription(data.sdp);
      rtcLog(`peer[${fromPeer}] ← received ${desc.type.toUpperCase()} (signalingState=${pc.signalingState})`);

      // Perfect-negotiation collision detection: if a remote OFFER arrives
      // while we're mid-offer OR our signalingState isn't stable, we have
      // "glare". The impolite side ignores; the polite side rolls back and
      // accepts. This guarantees convergence regardless of who joined first.
      const offerCollision =
        desc.type === 'offer'
        && (makingOfferRef.current[fromPeer] || pc.signalingState !== 'stable');
      const polite = isPoliteFor(fromPeer);
      if (offerCollision && !polite) {
        rtcWarn(`peer[${fromPeer}] OFFER collision — ignoring (I am impolite)`);
        return;
      }
      if (offerCollision && polite) {
        rtcLog(`peer[${fromPeer}] OFFER collision — rolling back (I am polite)`);
        // setRemoteDescription with implicit rollback (Chrome/Edge/Firefox all support this now)
      }

      try {
        await pc.setRemoteDescription(desc);
        remoteDescSetRef.current[fromPeer] = true;
        rtcLog(`peer[${fromPeer}] setRemoteDescription OK`);

        // Drain any ICE candidates that arrived before the remote description
        const queued = pendingCandidatesRef.current[fromPeer] || [];
        if (queued.length) {
          rtcLog(`peer[${fromPeer}] draining ${queued.length} queued ICE candidate(s)`);
          for (const c of queued) {
            try { await pc.addIceCandidate(c); }
            catch (e) { rtcWarn(`peer[${fromPeer}] queued addIceCandidate failed:`, e); }
          }
          pendingCandidatesRef.current[fromPeer] = [];
        }

        if (desc.type === 'offer') {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          rtcLog(`peer[${fromPeer}] → sending ANSWER (sdp length=${pc.localDescription.sdp?.length})`);
          wsRef.current?.send(JSON.stringify({
            type: 'signal',
            to: fromPeer,
            data: { sdp: pc.localDescription },
          }));
        }
      } catch (e) {
        rtcWarn(`peer[${fromPeer}] setRemoteDescription failed:`, e);
        console.error('setRemoteDescription failed', e);
      }
    } else if (data.candidate) {
      const parsed = parseCandidate(data.candidate);
      // If the remote description isn't set yet, queue the candidate. Once
      // setRemoteDescription runs, the queue is drained in the sdp-branch above.
      if (!remoteDescSetRef.current[fromPeer] || !pc.remoteDescription) {
        rtcLog(`peer[${fromPeer}] ← REMOTE ICE candidate (QUEUED — remote description not set yet):`, parsed);
        (pendingCandidatesRef.current[fromPeer] = pendingCandidatesRef.current[fromPeer] || []).push(data.candidate);
        return;
      }
      rtcLog(`peer[${fromPeer}] ← REMOTE ICE candidate:`, parsed);
      try {
        await pc.addIceCandidate(data.candidate);
      } catch (e) {
        rtcWarn(`peer[${fromPeer}] addIceCandidate failed:`, e);
        console.warn('addIceCandidate failed', e);
      }
    }
  }, [createPeerConnection, isPoliteFor]);

  // Proactively warn users who are in an environment where getUserMedia is
  // guaranteed to fail (in-app browsers, HTTP). We don't wait for the mic-tap
  // because the user might sit in the room for a while wondering why they hear
  // nothing. NotAllowedError still surfaces on tap — this only catches the
  // "environment is fundamentally broken" cases.
  useEffect(() => {
    const diag = diagnoseMicSupport();
    if (diag.blocked && (diag.reason === 'in-app' || diag.reason === 'no-media-api' || diag.reason === 'insecure')) {
      setMicBlockNotice({
        title: diag.reason === 'in-app' ? 'Åbn siden i Safari eller Chrome' : 'Mikrofon-adgang er blokeret',
        message: diag.actionable,
        appName: diag.appName,
      });
    }
  }, []);

  // Media-session keep-alive: silent audio + navigator.mediaSession so
  // iOS/Android don't kill the tab when the screen locks.
  //
  // ⚠️ MOBILE-ONLY (v1.0.20+): On desktop Chrome/Edge, the silent <audio>
  // element steals audio focus from WebRTC remote streams, causing "connected
  // but no sound" (regression discovered in v1.0.18). Desktop tabs don't need
  // this keep-alive anyway — desktop OSes don't aggressively suspend tabs
  // like iOS/Android do when the screen locks. So we gate the entire
  // keep-alive on isMobile() to get the best of both worlds:
  //   • Mobile: MediaSession active → tab survives screen lock, audio flows
  //   • Desktop: no MediaSession → WebRTC audio routing untouched
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!isMobile()) {
      // eslint-disable-next-line no-console
      console.log('%c[mediaSession]', 'color:#c0f000;font-weight:bold', 'skipped on desktop — not needed and avoids audio-focus conflict with WebRTC');
      return undefined;
    }
    startMediaSessionKeepalive({
      title: groupName || 'Samtale24-7',
      artist: 'Live samtale',
    });
    armMediaSessionOnGesture();
    return () => {
      stopMediaSessionKeepalive();
    };
  }, [groupName]);

  // Screen Wake Lock — MOBILE ONLY (v1.0.27+).
  //
  // Android Chrome aggressively releases microphone hardware when the screen
  // locks, EVEN when MediaSession keep-alive is active. This is the same
  // technique used by Google Meet, Zoom Web, and Discord Web on Android: ask
  // the OS to keep the screen from turning off while the user is in a call.
  // The screen can still dim per user setting, but never fully sleeps, so
  // the mic hardware slot is never released.
  //
  // The Wake Lock auto-releases when the tab becomes hidden (browser policy),
  // so we re-request it on `visibilitychange → visible`. This gives us
  // "screen stays on while user is looking at the call tab".
  //
  // Not applied on desktop: (a) desktop OSes don't release mic on screen
  // saver, (b) it would keep monitors on unnecessarily.
  useEffect(() => {
    if (typeof window === 'undefined' || !isMobile()) return undefined;
    if (!('wakeLock' in navigator)) {
      // eslint-disable-next-line no-console
      console.log('%c[wakeLock]', 'color:#c0f000;font-weight:bold', 'Wake Lock API not supported — screen may lock and release mic (Android Chrome < 84 / older browsers)');
      return undefined;
    }
    let wakeLock = null;
    let released = false;

    const request = async () => {
      if (released) return;
      try {
        wakeLock = await navigator.wakeLock.request('screen');
        // eslint-disable-next-line no-console
        console.log('%c[wakeLock]', 'color:#c0f000;font-weight:bold', 'screen wake lock acquired — screen will not turn off during call');
        wakeLock.addEventListener('release', () => {
          // eslint-disable-next-line no-console
          console.log('%c[wakeLock]', 'color:#c0f000;font-weight:bold', 'wake lock released by browser (tab hidden or manual)');
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('%c[wakeLock]', 'color:#c0f000;font-weight:bold', 'request failed (non-fatal):', e?.name, e?.message);
      }
    };

    const onVisibility = () => {
      // Browsers auto-release the wake lock when the tab is hidden. Re-request
      // when the user returns to the tab so the screen keeps staying on.
      if (!document.hidden && !released) {
        request();
      }
    };

    request();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try { wakeLock?.release?.(); } catch { /* noop */ }
      wakeLock = null;
    };
  }, []);

  // Main setup
  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      rtcLog('starting — groupId=', groupId, 'guestId=', guestId, 'displayName=', displayName);
      // 0. Fetch ICE servers (STUN + TURN) from backend before creating any RTCPeerConnection
      try {
        const { data } = await api.get('/rtc/ice-servers');
        if (Array.isArray(data.ice_servers) && data.ice_servers.length > 0) {
          iceServersRef.current = data.ice_servers;
          // Always-on: report what we got so we can confirm TURN is available.
          const dump = data.ice_servers.map(s => {
            const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
            return { urls, hasAuth: !!s.username };
          });
          const stunCount = dump.filter(s => s.urls.some(u => u.startsWith('stun:'))).length;
          const turnCount = dump.filter(s => s.urls.some(u => u.startsWith('turn:') || u.startsWith('turns:'))).length;
          rtcAlways(`ICE servers loaded from backend: ${data.ice_servers.length} total (${stunCount} STUN, ${turnCount} TURN)`, dump);
          setTurnAvailable(turnCount > 0);
          if (turnCount === 0) {
            rtcAlwaysWarn('NO TURN SERVERS in ICE config — cross-network peers will fail. Backend /api/rtc/ice-servers returned STUN-only.');
          }
        } else {
          setTurnAvailable(false);
          rtcAlwaysWarn('ICE servers response was EMPTY — WebRTC WILL fail on strict NATs. Check backend /api/rtc/ice-servers and Metered.ca credentials.');
        }
      } catch (e) {
        setTurnAvailable(false);
        rtcAlwaysWarn('ICE servers fetch FAILED — no TURN available:', e?.message || e);
        console.warn('ICE servers fetch failed, no TURN available:', e);
      }
      if (cancelled) return;

      // 1. NO getUserMedia here. We join the room silently — the mic (and email
      //    notification, which fires at WS-join on the backend) happen immediately.
      //    Microphone permission is requested lazily on first click of the mic button.

      // 2. Open WebSocket signaling
      const token = getToken();
      const backend = process.env.REACT_APP_BACKEND_URL;
      // Guest path — free-user gateway uses guest_id in place of vc_token
      const authParam = guestId
        ? `guest_id=${encodeURIComponent(guestId)}`
        : `token=${encodeURIComponent(token || '')}`;
      const wsUrl = backend.replace(/^http/, 'ws') + `/api/ws/rtc/${groupId}?${authParam}&display_name=${encodeURIComponent(displayName || 'Deltager')}`;
      rtcLog('opening WS:', wsUrl.replace(/token=[^&]+/, 'token=***').replace(/guest_id=[^&]+/, 'guest_id=***'));
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => { rtcLog('WS OPEN'); setStatus('ready'); };
      ws.onerror = (e) => {
        rtcWarn('WS ERROR:', e);
        setErrorMsg('Kunne ikke oprette forbindelse til signal-server.');
        setStatus('error');
      };
      ws.onclose = (ev) => {
        rtcLog(`WS CLOSE — code=${ev.code}, reason="${ev.reason}"`);
        if (ev.code === 4401) { setErrorMsg('Ikke autoriseret. Log ind igen.'); setStatus('error'); }
        else if (ev.code === 4403) { setErrorMsg('Aktivt abonnement kræves.'); setStatus('error'); }
        else if (ev.code === 4404) { setErrorMsg('Gruppe findes ikke.'); setStatus('error'); }
      };

      ws.onmessage = async (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch (e) { console.debug('bad signaling payload:', e); return; }
        // Trim SDP/candidate payloads in the log to keep it readable
        if (msg.type === 'signal') {
          rtcLog(`WS ← signal from ${msg.from}: ${msg.data?.sdp ? `SDP(${msg.data.sdp.type})` : msg.data?.candidate ? 'ICE-candidate' : 'unknown'}`);
        } else {
          rtcLog('WS ←', msg);
        }
        if (msg.type === 'welcome') {
          myPeerIdRef.current = msg.peer_id;
          // Play the Danish welcome voice-over (one-shot; ignored if another clip is playing)
          playVoiceOver(VOICE_WELCOME_URL);
          // Register every peer already in the room (drives the counter)
          const seed = {};
          for (const p of msg.peers || []) seed[p.peer_id] = p.name || 'Deltager';
          setConnectedPeers(seed);
          // Initiate offer to each already-present peer
          for (const p of msg.peers || []) {
            initiateOfferTo(p.peer_id, p.name);
          }
        } else if (msg.type === 'peer-joined') {
          // Existing peers wait for the newcomer to send them an offer.
          // Proactively create the RTCPeerConnection NOW (with any local
          // tracks already attached) so:
          //   1. Incoming ICE candidates have somewhere to go — the pc exists
          //      even before the offer arrives.
          //   2. Our audio track is part of the initial SDP answer, so remote
          //      audio starts flowing on the very first negotiation instead
          //      of requiring a follow-up renegotiate.
          setConnectedPeers((prev) => ({ ...prev, [msg.peer_id]: msg.name || 'Deltager' }));
          if (!peersRef.current[msg.peer_id]?.pc) {
            createPeerConnection(msg.peer_id, msg.name || 'Deltager');
          } else {
            peersRef.current[msg.peer_id].name = msg.name || peersRef.current[msg.peer_id].name;
          }
        } else if (msg.type === 'peer-left') {
          removePeer(msg.peer_id);
        } else if (msg.type === 'signal') {
          await handleSignal(msg.from, msg.data, peersRef.current[msg.from]?.name);
        } else if (msg.type === 'pong') {
          // Silently ignore keep-alive replies from the backend. Presence of
          // the frame is enough — no action needed.
        }
      };
    };

    start();

    return () => {
      cancelled = true;
      // Cleanup
      try { wsRef.current?.send(JSON.stringify({ type: 'leave' })); } catch (e) { console.debug('ws leave send failed:', e); }
      try { wsRef.current?.close(); } catch (e) { console.debug('ws close failed:', e); }
      Object.values(peersRef.current).forEach((p) => { try { p.pc?.close(); } catch (e) { console.debug('peer close failed:', e); } });
      peersRef.current = {};
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [groupId]);

  // One-time "unmute all remote videos" handler. iOS Safari and mobile Chrome
  // apply autoplay heuristics that can leave <video> elements paused/muted
  // even after srcObject is set — until there's an explicit user gesture.
  // Triggered on:
  //   1. First tap of the mic button (below in toggleMic)
  //   2. Any pointerdown anywhere inside the room overlay (fallback for
  //      "listen-only" guests who never tap the mic)
  // Idempotent via unmutedRemotesOnceRef so multiple triggers are safe.
  const unmutedRemotesOnceRef = useRef(false);
  const unmuteAllRemoteVideos = useCallback(() => {
    if (unmutedRemotesOnceRef.current) return;
    unmutedRemotesOnceRef.current = true;
    const els = document.querySelectorAll('video[data-remote-video="1"], audio[data-remote-video="1"]');
    els.forEach((el) => {
      try { el.muted = false; } catch { /* noop */ }
      try { el.volume = 1.0; } catch { /* noop */ }
      el.play?.().catch(() => { /* still blocked — nothing we can do */ });
    });
  }, []);

  // ── Page Visibility keep-alive ──────────────────────────────────────────
  // When the tab is hidden or the phone screen locks, browsers throttle
  // JavaScript timers (>=1min intervals) and mobile carriers/proxies may
  // close idle WebSockets. Instead of grabbing a Wake Lock (which drains
  // battery), we ride the natural sleep cycle and keep just the WS alive.
  //   • On visibilitychange → hidden : start a 10s ping interval
  //   • On visibilitychange → visible: stop pings, then for each peer whose
  //     connectionState/iceConnectionState is 'failed' or 'disconnected',
  //     kick an ICE restart to recover any dead RTCPeerConnection.
  // Timers are cleaned up on unmount.
  useEffect(() => {
    let pingInterval = null;

    const startPings = () => {
      if (pingInterval) return;
      pingInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          try { wsRef.current.send(JSON.stringify({ type: 'ping' })); } catch { /* noop */ }
        }
      }, 10000);
    };

    const stopPings = () => {
      if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    };

    const onVisibilityChange = async () => {
      if (document.hidden) {
        rtcLog('page hidden — starting 10s WS ping keep-alive');
        startPings();
      } else {
        rtcLog('page visible — stopping pings, checking peer states');
        stopPings();
        // For every peer whose connection is broken, kick an ICE restart.
        // Reuses the same scheduleIceRestart machinery (respects polite/impolite
        // rule so only one side of each pair actually restarts).
        for (const [peerId, entry] of Object.entries(peersRef.current)) {
          const pc = entry?.pc;
          if (!pc) continue;
          const cs = pc.connectionState;
          const ics = pc.iceConnectionState;
          if (cs === 'failed' || cs === 'disconnected' || ics === 'failed' || ics === 'disconnected') {
            rtcAlways(`page-visible recovery: peer[${peerId}] state=${cs}/${ics} — triggering ICE restart`);
            // Reset the auto-retry counter so this attempt gets a fresh chance
            // (the counter may already be at 1 from prior failures).
            iceRestartAttemptsRef.current[peerId] = 0;
            scheduleIceRestart(peerId, /*immediate*/ true);
          }
        }
      }
      // Re-acquire the local mic if the OS suspended it during screen lock.
      // Mobile Safari/Chrome put audio input tracks into `muted=true` or
      // `readyState='ended'` state when the phone locks. Desktop tabs are
      // NOT affected — the check below is a no-op on PC because the track's
      // `muted`/`ended` state stays clean, so this cannot regress the working
      // desktop flow.
      const local = localStreamRef.current;
      if (local && local.getAudioTracks) {
        const audioTracks = local.getAudioTracks();
        const needsRefresh = audioTracks.some(
          (t) => t.muted === true || t.readyState === 'ended'
        );
        if (needsRefresh) {
          rtcAlways('page-visible: local mic track was suspended by OS — re-acquiring');
          try {
            const fresh = await navigator.mediaDevices.getUserMedia({
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
            });
            const newTrack = fresh.getAudioTracks()[0];
            if (newTrack) {
              // Replace on every existing peer connection via RTCRtpSender.replaceTrack
              // (avoids renegotiation — much smoother than remove+add).
              for (const entry of Object.values(peersRef.current)) {
                const sender = entry?.pc?.getSenders?.().find((s) => s.track && s.track.kind === 'audio');
                if (sender) {
                  try { await sender.replaceTrack(newTrack); }
                  catch (e) { rtcWarn('replaceTrack failed on peer, will fall through:', e); }
                }
              }
              // Stop the old suspended tracks, then swap the local stream
              audioTracks.forEach((t) => { try { t.stop(); } catch { /* noop */ } });
              localStreamRef.current = fresh;
              // Respect current mic UI state — if user had turned mic OFF before
              // the screen lock, keep it off on the fresh track.
              newTrack.enabled = micOn;
              rtcAlways('page-visible: local mic re-acquired and replaced on all peers');
            }
          } catch (e) {
            rtcWarn('page-visible: failed to re-acquire mic:', e?.name, e?.message);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopPings();
    };
  }, [scheduleIceRestart, micOn]);

  const toggleMic = async () => {
    // First tap is the user gesture that lets us safely unmute + play all
    // remote video elements — required on iOS Safari & mobile Chrome.
    unmuteAllRemoteVideos();
    // Lazy mic acquisition: first click prompts for permission and adds the
    // audio track to every existing peer connection (renegotiation is handled by
    // pc.onnegotiationneeded). Subsequent clicks just toggle the track's enabled state.
    if (!localStreamRef.current) {
      // Precheck: catch every environment where getUserMedia is guaranteed to
      // fail (in-app WebViews, HTTP, missing API). Show a persistent, actionable
      // overlay instead of a fleeting toast.
      const diag = diagnoseMicSupport();
      if (diag.blocked) {
        setMicBlockNotice({
          title: diag.reason === 'in-app'
            ? `Åbn siden i Safari eller Chrome`
            : 'Mikrofon-adgang er blokeret',
          message: diag.actionable,
          appName: diag.appName,
        });
        return;
      }
      // Permissions API pre-check: if permission was previously denied (Edge,
      // Chrome, Firefox all remember this per-origin), getUserMedia will throw
      // NotAllowedError WITHOUT re-prompting. Detect this up front so we can
      // give the user the exact browser-specific unblock steps instead of a
      // generic "denied" message.
      const permState = await queryMicPermission();
      if (permState === 'denied') {
        const instr = getUnblockInstructions();
        setMicBlockNotice({ title: instr.title, message: instr.message });
        return;
      }
      try {
        // Audio-only — explicitly omit the `video` key so no browser can misinterpret
        // it as "ask for both mic and camera" (some mobile Safaris used to prompt for
        // both when `video: false` was present).
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          // Respect any current forceMuted state
          audioTrack.enabled = !forceMuted;
          // Add the newly-acquired mic to every existing peer connection.
          // pc.onnegotiationneeded (installed in createPeerConnection) will
          // automatically issue a new offer to the other side so audio starts flowing.
          Object.values(peersRef.current).forEach(({ pc }) => {
            if (!pc) return;
            try { pc.addTrack(audioTrack, stream); } catch (e) { console.debug('addTrack failed:', e); }
          });
        }
        setMicOn(true);
      } catch (e) {
        console.error('getUserMedia (mic) failed:', e?.name, e?.message);
        const name = e?.name || '';
        // NotAllowedError: user just clicked "Block" OR browser has silently
        // denied (Edge Tracking Prevention, Windows Privacy setting, InPrivate
        // mode, admin policy). Show browser-specific unblock instructions +
        // include the raw error message so users can share it for support.
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
          const instr = getUnblockInstructions();
          const techDetail = e?.message ? `\n\n(Teknisk: ${name} — ${e.message})` : `\n\n(Teknisk: ${name})`;
          setMicBlockNotice({
            title: instr.title,
            message: instr.message + techDetail,
          });
          return;
        }
        let msg;
        switch (name) {
          case 'NotFoundError':
          case 'DevicesNotFoundError':
            msg = 'Ingen mikrofon fundet på enheden. Tilslut en mikrofon og prøv igen.';
            break;
          case 'NotReadableError':
          case 'TrackStartError':
            msg = 'Din mikrofon er optaget af et andet program. Luk andre apps der bruger mikrofonen og prøv igen.';
            break;
          case 'OverconstrainedError':
            msg = 'Din enhed understøtter ikke de valgte audio-indstillinger. Prøv en anden browser.';
            break;
          case 'AbortError':
            msg = 'Mikrofon-anmodningen blev afbrudt. Prøv igen.';
            break;
          case 'TypeError':
            msg = 'Din browser tillader ikke mikrofon her. Åbn siden direkte i Safari eller Chrome (ikke via en app-webview).';
            break;
          default:
            msg = `Kunne ikke få adgang til mikrofonen (${name || 'ukendt fejl'}). Tjek browser-indstillinger og prøv igen.`;
        }
        toast.error(msg);
      }
      return;
    }
    const stream = localStreamRef.current;
    const nextEnabled = !(stream.getAudioTracks()[0]?.enabled ?? false);
    stream.getAudioTracks().forEach((t) => { t.enabled = nextEnabled; });
    setMicOn(nextEnabled);
  };

  // Hard mute: disable local audio track + mute all remote <video> elements when forceMuted=true
  useEffect(() => {
    const stream = localStreamRef.current;
    if (stream) {
      stream.getAudioTracks().forEach((t) => { t.enabled = forceMuted ? false : micOn; });
    }
    // Mute/unmute all remote video elements' speaker output
    document.querySelectorAll('[data-testid^="video-tile-"] video').forEach((v) => {
      // Never touch the local tile (it's already muted for echo prevention)
      if (v.getAttribute('data-local') !== '1') v.muted = !!forceMuted;
    });
  }, [forceMuted, micOn]);

  const toggleCam = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
    setCamOn(stream.getVideoTracks()[0]?.enabled ?? false);
  };

  const totalTiles = Object.keys(remotePeers).length + 1;
  const voicePlaying = useVoiceOverPlaying();

  // Play farewell then invoke onLeave (used by Forlad-btn + Tilbage-btn)
  // Waits for the audio to finish before closing the room — with a hard safety timeout.
  // If skipFarewell=true, the parent handles the farewell playback (e.g., FreeGroupJoined).
  const handleLeaveClick = () => {
    if (skipFarewell) {
      onLeave && onLeave();
      return;
    }
    // Play farewell through the singleton — if a welcome is still going, this
    // request is skipped (never stacks) and we leave immediately instead.
    const audio = playVoiceOver(VOICE_FAREWELL_URL, { onEnd: () => onLeave && onLeave() });
    if (!audio) {
      // Another voice-over was already playing; leave straight away.
      onLeave && onLeave();
    }
  };

  const gridCols =
    totalTiles === 1 ? 'grid-cols-1' :
    totalTiles === 2 ? 'grid-cols-1 md:grid-cols-2' :
    totalTiles <= 4 ? 'grid-cols-1 md:grid-cols-2' :
    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  // Build a real-time aggregate connection status text for the on-screen pill.
  // Combines WebSocket state + per-peer WebRTC states so users/testers can
  // pinpoint exactly where the connection stops (WS handshake, ICE, or media).
  const connStatusInfo = (() => {
    // 1) WebSocket layer
    if (status === 'error') {
      return { label: `Fejl: ${errorMsg || 'signal-server utilgængelig'}`, color: 'destructive' };
    }
    if (status === 'starting') {
      return { label: 'Forbinder til signal-server…', color: 'yellow' };
    }
    // 2) WebSocket is OPEN — check peer connections
    const peerIds = Object.keys(connectedPeers);
    if (peerIds.length === 0) {
      // If TURN is missing, be honest up-front so users understand why the
      // room may only work with peers on the same network.
      if (!turnAvailable) {
        return {
          label: 'Forbundet. Bemærk: TURN-server utilgængelig — samtaler på tværs af netværk kan fejle.',
          color: 'yellow',
        };
      }
      return { label: 'Forbundet. Venter på andre deltagere…', color: 'volt' };
    }
    const states = peerIds.map((pid) => peerConnStates[pid] || 'new');
    const total = peerIds.length;
    const connected = states.filter((s) => s === 'connected').length;
    const failed = states.filter((s) => s === 'failed').length;
    const disconnected = states.filter((s) => s === 'disconnected').length;
    const closed = states.filter((s) => s === 'closed').length;
    const pending = total - connected - failed - disconnected - closed; // 'new' | 'connecting'
    if (failed > 0) {
      const reason = turnAvailable
        ? 'ICE mislykkedes — evt. TURN blokeret af firewall'
        : 'TURN-server utilgængelig fra backend';
      return { label: `Forbindelse mislykkedes med ${failed}/${total} deltager(e) (${reason})`, color: 'destructive' };
    }
    if (disconnected > 0) {
      return { label: `Forbindelsen tabt til ${disconnected}/${total} — genforbinder…`, color: 'yellow' };
    }
    if (pending > 0) {
      return { label: `Forbinder til ${pending}/${total} deltager(e)…`, color: 'yellow' };
    }
    if (connected === total) {
      return { label: `Forbundet med ${total} deltager(e)`, color: 'volt' };
    }
    return { label: `Status: ${connected} forbundet, ${pending} venter`, color: 'yellow' };
  })();

  const connStatusClasses =
    connStatusInfo.color === 'destructive' ? 'border-destructive/60 bg-destructive/15 text-destructive' :
    connStatusInfo.color === 'yellow' ? 'border-yellow-500/50 bg-yellow-500/10 text-yellow-200' :
    'border-volt/50 bg-volt/10 text-volt';
  const connStatusDotClasses =
    connStatusInfo.color === 'destructive' ? 'bg-destructive' :
    connStatusInfo.color === 'yellow' ? 'bg-yellow-500 animate-pulse' :
    'bg-volt pulse-dot';

  return (
    <div
      className="fixed inset-0 z-30 flex flex-col bg-[#09090B]"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="video-room-overlay"
      onPointerDown={unmuteAllRemoteVideos}
    >
      {/* Header — group name + status (counter moved below the controls pill) */}
      <div className="flex items-center justify-between gap-2 px-3 md:px-6 py-3 border-b border-border shrink-0">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground hidden md:block">I gruppe</div>
          <div className="font-display text-sm md:text-xl font-bold truncate" data-testid="joined-group-name">{groupName}</div>
        </div>
        <div className="flex items-center gap-1.5 md:gap-3 text-xs shrink-0 flex-none">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`w-2 h-2 rounded-full ${status === 'ready' ? 'bg-volt pulse-dot' : status === 'error' ? 'bg-destructive' : 'bg-yellow-500 animate-pulse'}`} />
            <span className="text-muted-foreground hidden md:inline">
              {status === 'ready' ? 'Live' : status === 'error' ? 'Fejl' : 'Forbinder…'}
            </span>
          </div>
        </div>
      </div>

      {/* Floating controls pill — anchored INSIDE the overlay (which already
          accounts for safe-area-inset-top), so it always clears mobile browser
          address bars and iOS notches. */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-20 z-50 flex items-center gap-2 rounded-full bg-black/70 backdrop-blur-md border border-white/10 px-3 py-2 shadow-lg"
        data-testid="video-room-controls"
      >
        <button
          onClick={toggleMic}
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95 ${micOn ? 'bg-[#27272A] hover:bg-[#3f3f46] text-white' : 'bg-destructive hover:bg-destructive/90 text-white'}`}
          title={micOn ? 'Sluk mikrofon' : 'Tænd mikrofon'}
          aria-label={micOn ? 'Sluk mikrofon' : 'Tænd mikrofon'}
          data-testid="toggle-mic-btn"
        >
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button
          onClick={toggleCam}
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95 ${camOn ? 'bg-[#27272A] hover:bg-[#3f3f46] text-white' : 'bg-destructive hover:bg-destructive/90 text-white'} ${withVideo ? '' : 'hidden'}`}
          title={camOn ? 'Sluk kamera' : 'Tænd kamera'}
          aria-label={camOn ? 'Sluk kamera' : 'Tænd kamera'}
          data-testid="toggle-cam-btn"
        >
          {camOn ? <VideoOn className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        <button
          onClick={handleLeaveClick}
          className="h-11 px-4 rounded-full bg-destructive hover:bg-destructive/90 text-white font-semibold flex items-center gap-2 transition-all active:scale-95"
          aria-label="Forlad gruppe"
          data-testid="leave-group-btn"
        >
          <PhoneOff className="w-5 h-5" />
          <span className="hidden sm:inline">Forlad</span>
        </button>
      </div>

      {/* Supervisor / Admin counter — sits directly BELOW the control pill,
          also centered, so it's always visible next to mic/leave. */}
      {showConnectedCount && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-36 z-50 flex items-center gap-1.5 rounded-full border border-volt/50 bg-volt/10 px-3 py-1.5 text-volt font-semibold text-xs backdrop-blur-md"
          data-testid="supervisor-counter"
          title="Kun synlig for admin & supervisorer"
        >
          <Users className="w-3.5 h-3.5 shrink-0" />
          <span>Deltagere:</span>
          <span className="font-bold" data-testid="supervisor-counter-value">
            {Object.keys(connectedPeers).length + 1}
          </span>
        </div>
      )}

      {/* Real-time connection status pill — shows WS + WebRTC state so users
          and testers can see exactly where the connection stops. Placed just
          under the supervisor counter (or the control pill when the counter
          is hidden). Uses whitespace-normal + no max-width so the full error
          message is always visible on multiple lines instead of being truncated. */}
      {showConnectionStatus && (
        <div
          className={`absolute left-1/2 -translate-x-1/2 ${showConnectedCount ? 'top-52' : 'top-36'} z-50 flex items-start gap-2 rounded-2xl border px-4 py-4 font-semibold text-sm backdrop-blur-md max-w-[95vw] min-h-[64px] ${connStatusClasses}`}
          data-testid="connection-status-pill"
          title="Live WebRTC forbindelse-status"
        >
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ${connStatusDotClasses}`} />
          <span className="whitespace-normal break-words leading-relaxed" data-testid="connection-status-text">
            {connStatusInfo.label}
          </span>
        </div>
      )}

      {/* Manual retry banner — shown after the automatic retry has failed.
          Full-width, clickable card with a "Prøv igen" button that tears down
          the failed peer connection(s) and starts a fresh negotiation. */}
      {Object.keys(peersNeedingManualRetry).length > 0 && (
        <button
          onClick={manualRetryConnection}
          className="absolute left-1/2 -translate-x-1/2 bottom-24 z-[60] flex items-center gap-3 rounded-2xl border-2 border-destructive bg-destructive/90 hover:bg-destructive text-white px-6 py-4 font-bold shadow-2xl backdrop-blur-md max-w-[95vw] transition-all active:scale-95"
          data-testid="manual-retry-banner"
          title="Fuldt genforbind til den/de fejlede deltager(e)"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-white shrink-0 animate-pulse" />
          <span className="whitespace-normal break-words leading-snug text-left" data-testid="manual-retry-text">
            Forbindelsen mislykkedes — tryk her for at prøve igen
          </span>
        </button>
      )}

      {/* Voice-over blocker: while a welcome/farewell clip is playing, prevent
          ALL user interaction (mic-toggle, leave, popups) so clips can't stack.
          Suppressed on the free-group page (hideParticipantInfo=true) — the
          mutex in voiceOver.js still prevents audio stacking, we just don't
          need the visual "Afspiller besked…" pill there. */}
      {voicePlaying && !hideParticipantInfo && (
        <div
          className="absolute inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm cursor-wait select-none"
          data-testid="voiceover-blocker"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-3 rounded-full bg-black/70 border border-white/10 px-5 py-3 text-white text-sm">
            <span className="w-2 h-2 rounded-full bg-volt animate-pulse" />
            <span>Afspiller besked…</span>
          </div>
        </div>
      )}

      {/* Persistent mic-block overlay — shown when the environment guarantees
          getUserMedia will fail (in-app browser, HTTP, permission denied on iOS).
          A dismissible toast is not enough on mobile; users need explicit steps. */}
      {micBlockNotice && (
        <div
          className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-md px-6"
          data-testid="mic-block-overlay"
        >
          <div className="max-w-md w-full max-h-[85vh] overflow-y-auto rounded-lg border border-destructive/50 bg-[#18181B] p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center shrink-0">
                <MicOff className="w-5 h-5 text-destructive" />
              </div>
              <h3 className="font-display text-lg font-bold" data-testid="mic-block-title">
                {micBlockNotice.title}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground whitespace-pre-line" data-testid="mic-block-message">
              {micBlockNotice.message}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={() => {
                  try {
                    navigator.clipboard?.writeText(window.location.href);
                    toast.success('Link kopieret — indsæt i Safari eller Chrome');
                  } catch { toast.error('Kunne ikke kopiere linket'); }
                }}
                className="bg-volt text-black hover:bg-volt font-semibold flex-1"
                data-testid="mic-block-copy-link-btn"
              >
                Kopier link
              </Button>
              <Button
                onClick={() => setMicBlockNotice(null)}
                variant="outline"
                className="border-border flex-1"
                data-testid="mic-block-dismiss-btn"
              >
                Luk
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Video grid (controls have been moved into the header above) */}
      <div className="flex-1 min-h-0 overflow-auto p-3 md:p-6">
        {status === 'error' ? (
          <div className="h-full flex items-center justify-center">
            <div className="max-w-md text-center rounded-lg border border-destructive/30 bg-[#18181B] p-8">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <VideoOff className="w-6 h-6 text-destructive" />
              </div>
              <h3 className="font-display text-xl font-bold mb-2">Kunne ikke starte video</h3>
              <p className="text-sm text-muted-foreground mb-6" data-testid="video-error-message">{errorMsg}</p>
              <Button onClick={handleLeaveClick} variant="outline" className="border-border">Tilbage</Button>
            </div>
          </div>
        ) : (
          <div className={`grid ${gridCols} gap-3 md:gap-4 h-full`} data-testid="video-tiles">
            {/* Local tile */}
            <VideoTile
              ref={localVideoRef}
              muted
              mirror
              name={hideParticipantInfo ? '' : `${myNickname || displayName} (dig)`}
              camOn={camOn}
              testId="video-tile-local"
            />
            {/* Remote tiles */}
            {Object.entries(remotePeers).map(([pid, info]) => (
              <RemoteVideoTile key={pid} peerId={pid} stream={info.stream} name={hideParticipantInfo ? '' : (info.name || 'Deltager')} />
            ))}
          </div>
        )}
      </div>

      {/* Controls have been moved to the header (top-right) to avoid being
          hidden by page-level bottom overlays (e.g., free-group popups). */}
    </div>
  );
}

// Local video tile (uses ref)
import { forwardRef } from 'react';
const VideoTile = forwardRef(function VideoTile({ muted, mirror, name, camOn, testId }, ref) {
  return (
    <div className="relative rounded-lg overflow-hidden bg-[#18181B] border border-border aspect-video min-h-0" data-testid={testId}>
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        data-local="1"
        className={`w-full h-full object-cover ${mirror ? '-scale-x-100' : ''}`}
      />
      {!camOn && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0d0d10]">
          <div className="w-16 h-16 rounded-full bg-[#27272A] flex items-center justify-center">
            <User className="w-8 h-8 text-muted-foreground" />
          </div>
        </div>
      )}
      {name ? (
        <div className="absolute bottom-2 left-2 text-xs bg-black/70 backdrop-blur px-2 py-1 rounded font-medium">
          {name}
        </div>
      ) : null}
    </div>
  );
});

// Remote video tile (attaches stream via ref).
// Explicitly play() + unmute the element every time the stream changes so iOS
// Safari can't leave it silently paused/muted after autoplay heuristics kick in.
function RemoteVideoTile({ peerId, stream, name }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;
    el.srcObject = stream;
    try { el.muted = false; } catch { /* noop */ }
    // .play() may reject if the user hasn't gestured yet — swallow the error;
    // the mic-button click below will unmute+play all remote videos globally.
    el.play?.().catch(() => { /* pending gesture */ });
    // Tag element so the global "unmute on first tap" handler can find it.
    el.setAttribute('data-remote-video', '1');
  }, [stream]);
  return (
    <div className="relative rounded-lg overflow-hidden bg-[#18181B] border border-border aspect-video min-h-0" data-testid={`video-tile-${peerId}`}>
      <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
      {name ? (
        <div className="absolute bottom-2 left-2 text-xs bg-black/70 backdrop-blur px-2 py-1 rounded font-medium">
          {name}
        </div>
      ) : null}
    </div>
  );
}
